import * as React from 'react'
import { cn } from '@/lib/cn'

/**
 * Voice entries — the brand's signature layout device.
 *
 * A fugue is built from voices entering in sequence: subject, answer,
 * countersubject. Each is independent, and together they are one structure. That
 * is exactly what the product is (capabilities you turn on one at a time, all on
 * one system), and it is a device no competitor can borrow, because it comes from
 * the name.
 *
 * So a list of capabilities is not a bullet list. It is an exposition: each entry
 * steps in slightly further than the last, with a brass rule that lengthens as the
 * voices accumulate. That does real work on these pages — it replaces the
 * undifferentiated check-mark columns that made the site read as a wall of text,
 * and it encodes something true (these compose) rather than decorating.
 *
 * Deliberately NOT numbered `01 / 02 / 03`: these are simultaneous voices, not a
 * sequence, and numbering would assert an order the content does not have. Use
 * numbering only where the content really is stepwise (the "How it works" flow).
 */
interface VoiceEntryListProps extends React.ComponentProps<'ul'> {
  children: React.ReactNode
  /** Cap the indent so long lists do not march off the right edge. */
  maxStep?: number
  /** Class for each `<li>`. Lets a scroll-reveal hook target the items. */
  itemClassName?: string
}

export function VoiceEntryList({ children, maxStep = 3, itemClassName, className, ...props }: VoiceEntryListProps) {
  const items = React.Children.toArray(children)
  const single = items.length === 1
  return (
    <ul className={cn('space-y-3', className)} {...props}>
      {items.map((child, i) => (
        <li
          key={i}
          // The stagger. Each voice enters a step further in than the last, up to
          // `maxStep`, so the group reads as accumulating rather than as a stack.
          // A one-item list gets no indent — there is nothing to accumulate.
          style={single ? undefined : { paddingLeft: `${Math.min(i, maxStep) * 0.625}rem` }}
          className={itemClassName}
        >
          <div className="flex items-baseline gap-3">
            <span
              aria-hidden
              // A brass dot, not a rule. This was a hairline dash that lengthened
              // with each entry; combined with the eyebrow's leading dash the page
              // turned into a field of small horizontal strokes. The dot keeps the
              // brass and the entry point without reading as punctuation.
              className="bg-accent/70 mt-[0.45rem] size-1 shrink-0 self-start rounded-full"
            />
            <span className="text-muted-foreground text-fluid-body-sm">{child}</span>
          </div>
        </li>
      ))}
    </ul>
  )
}
